import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingDown, Quote, User, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import SectionHeading from "./SectionHeading";

export const CLIENTS = [
  {
    id: "vaibhav-mohod",
    name: "Vaibhav Mohod",
    tag: "Busy Professional",
    clientTag: "Working Professional",
    before: "92 kg",
    after: "80 kg",
    result: "-12 kg Fat Loss",
    duration: "12 Weeks",
    story: "Visceral fat loss while balancing long work hours with home-cooked Indian meals.",
  },
  {
    id: "ashwini-deulkar",
    name: "Ashwini Deulkar",
    tag: "Weight Loss",
    clientTag: "Lifestyle Recomp",
    before: null,
    after: null,
    result: "-15+ kg Lifestyle Cut",
    duration: "4 Months",
    story: "Metabolic reset and complete body recomposition through structured nutrition.",
  },
  {
    id: "monika-amravati",
    name: "Monika (Amravati)",
    tag: "Inch Loss & Toning",
    clientTag: "Lifestyle Recomp",
    before: "62 kg",
    after: "51 kg",
    result: "-11 kg Inch Loss",
    duration: "90 Days",
    story: "Multiple dress sizes down with consistent macro tracking and home workouts.",
  },
  {
    id: "mayuri",
    name: "Mayuri",
    tag: "Weight Loss",
    clientTag: "Lifestyle Recomp",
    before: "76 kg",
    after: "65 kg",
    result: "-11 kg Fat Loss",
    duration: "3 Months",
    story: "Postural improvement and sustained daily energy.",
  },
  {
    id: "pooja-amravati",
    name: "Pooja (Amravati)",
    tag: "Inch Loss & Toning",
    clientTag: "Lifestyle Recomp",
    before: "71 kg",
    after: "67 kg",
    result: "-4 kg & Inch Loss",
    duration: "90 Days",
    story: "Noticeable waist trimming and lean toning.",
  },
  {
    id: "shraddha-bisne",
    name: "Shraddha Bisne",
    tag: "Inch Loss & Toning",
    clientTag: "Lifestyle Recomp",
    before: null,
    after: null,
    result: "-6 kg Lifestyle Cut",
    duration: "8-10 Weeks",
    story: "Broke through stubborn plateaus with protein-forward nutrition.",
  },
  {
    id: "rashmi-shrirao",
    name: "Rashmi Shrirao",
    tag: "Weight Loss",
    clientTag: "Lifestyle Recomp",
    before: "66 kg",
    after: "57 kg",
    result: "-9 kg Fat Loss",
    duration: "12 Weeks",
    story: "Sustainable fat cut without cutting out daily Indian staples.",
  },
  {
    id: "snehal-pune",
    name: "Snehal (Pune)",
    tag: "Busy Professional",
    clientTag: "Working Professional",
    before: "66 kg",
    after: "56 kg",
    result: "-10 kg Fat Loss",
    duration: "3 Months",
    story: "10 kgs down — chronic acidity gone, 8-year migraine under control, deep restful sleep.",
  },
  {
    id: "aarti-nalat",
    name: "Aarti Nalat Shrirao",
    tag: "Weight Loss",
    clientTag: "Lifestyle Recomp",
    before: "76 kg",
    after: "66 kg",
    result: "-10 kg Lifestyle Cut",
    duration: "3 Months",
    story: "Down 10 kilos, from 76 to 66, with a structured nutrition plan.",
  },
];

const TABS = ["All", "Weight Loss (-10kg+)", "Inch Loss & Toning", "Busy Professional"];

const QUOTES = [
  "Down 2 dress sizes in 60 days!",
  "No weakness or starving.",
];

const initialsOf = (name) =>
  name.replace(/\(.*?\)/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

function ClientCard({ c, i, photo }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.45, delay: i * 0.05 }}
      className="flex flex-col rounded-3xl border border-line bg-panel p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-accent/50 hover:shadow-glow-emerald"
      data-testid={`client-card-${i}`}
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-ink via-panel to-ink flex flex-col items-center justify-center gap-4">
        {photo ? (
          <img
            src={photo.url.startsWith("http") ? photo.url : `${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
            alt={`${c.name} transformation result`}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            data-testid={`client-photo-${c.id}`}
          />
        ) : (
          <>
            <span className="flex h-20 w-20 items-center justify-center rounded-full bg-accent/10 border border-accent/30 font-display text-2xl font-black text-emerald-300">
              {initialsOf(c.name)}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ink/70 border border-line font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <User className="h-3 w-3" /> Client Photo Slot
            </span>
          </>
        )}
        {c.duration && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-ink/80 border border-gold/30 font-mono text-[10px] font-bold uppercase tracking-widest text-gold backdrop-blur-sm">
            {c.duration}
          </span>
        )}
      </div>
      <h3 className="mt-5 font-display text-xl font-extrabold uppercase text-white">{c.name}</h3>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {c.before && (
          <span className="px-2.5 py-1 rounded-full bg-ink border border-line font-mono text-xs text-slate-400">
            {c.before}
          </span>
        )}
        {c.before && <span className="font-mono text-xs text-slate-600">→</span>}
        {c.after && (
          <span className="px-2.5 py-1 rounded-full bg-ink border border-line font-mono text-xs text-slate-400">
            {c.after}
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent/15 border border-accent/40 font-mono text-xs font-extrabold text-emerald-300" data-testid={`client-result-${c.id}`}>
          <TrendingDown className="h-3 w-3" /> {c.result}
        </span>
      </div>
      <p className="mt-4 text-sm text-slate-400 leading-relaxed flex-1">"{c.story}"</p>
      <div className="mt-4 pt-4 border-t border-line flex items-center justify-between gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{c.tag}</p>
        <span
          className="px-2.5 py-0.5 rounded-full bg-accent/10 border border-accent/30 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-300"
          data-testid={`client-tag-${c.id}`}
        >
          {c.clientTag}
        </span>
      </div>
    </motion.div>
  );
}

const fromApi = (d) => ({
  id: d.slug || d.id,
  name: d.name,
  tag: d.tab,
  clientTag: d.category_tag,
  before: d.before || null,
  after: d.after || null,
  result: d.result,
  duration: d.duration,
  story: d.quote,
  imageUrl: d.image_url || "",
});

export default function Transformations() {
  const [tab, setTab] = useState("All");
  const [photos, setPhotos] = useState({});
  const [cards, setCards] = useState(CLIENTS);

  useEffect(() => {
    api("/transformations/photos")
      .then(setPhotos)
      .catch(() => {});
    api("/transformations/cards")
      .then((list) => {
        if (Array.isArray(list) && list.length) setCards(list.map(fromApi));
      })
      .catch(() => {});
  }, []);

  const filtered = tab === "All" ? cards : cards.filter((c) => c.tag === tab);

  return (
    <section id="transformations" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="Client Transformations"
          title="Real Clients. Real Numbers."
          sub="Verified results from working professionals and homemakers — built on custom nutrition, not crash diets."
        />
        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="transformation-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`transformation-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-all duration-300 ${
                tab === t
                  ? "bg-accent text-ink border-accent shadow-glow-emerald"
                  : "bg-panel text-slate-300 border-line hover:border-accent/50"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          <AnimatePresence mode="popLayout">
            {filtered.map((c, i) => (
              <ClientCard
                key={c.id}
                c={c}
                i={i}
                photo={c.imageUrl ? { url: c.imageUrl } : photos[c.id]}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        <div className="mt-14 border-y border-line bg-panel/60 py-4 overflow-hidden relative" data-testid="testimonial-ticker">
          <span className="hidden sm:inline-flex absolute left-0 top-0 h-full items-center px-4 bg-accent text-ink font-mono text-[10px] font-bold uppercase tracking-[0.2em] z-10 rounded-r-lg">
            <MessageCircle className="h-3.5 w-3.5 mr-2" /> WhatsApp
          </span>
          <div className="flex w-max animate-marquee">
            {[0, 1].map((copy) => (
              <ul key={copy} className="flex items-center gap-12 pr-12 shrink-0" aria-hidden={copy === 1 || undefined}>
                {QUOTES.map((q) => (
                  <li key={q} className="flex items-center gap-3 font-mono text-sm text-slate-300">
                    <Quote className="h-4 w-4 text-gold shrink-0" />
                    <span className="italic">"{q}"</span>
                    <span className="text-slate-600 text-xs">— Client, WhatsApp</span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}